import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getLocaleScreens } from '../utils/localeScreens';

const AUTOPLAY_MS = 3200;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const update = () => setReduced(Boolean(mq.matches));
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  return reduced;
}

export default function AppShowcasePhone() {
  const { t } = useTranslation();
  const { language } = useParams();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeIdx, setActiveIdx] = useState(0);
  const [isHover, setIsHover] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const rootRef = useRef(null);
  const touchRef = useRef({ x: 0, y: 0, t: 0 });

  const slideAssets = useMemo(() => getLocaleScreens(language), [language]);

  const slides = useMemo(() => {
    const slideCopy = t('home.app_showcase.slides', { returnObjects: true });
    const copyArray = Array.isArray(slideCopy) ? slideCopy : [];

    const fallback = [
      { title: 'Support when you need it', subtitle: 'An AI companion for everyday moments' },
      { title: 'One small step now', subtitle: 'Quick insights you can put into practice' },
      { title: 'Start with what’s on your mind', subtitle: 'Choose a topic or begin in your own words' },
      { title: 'Understand how you’re feeling', subtitle: 'Standardized questionnaires for self-reflection' },
      { title: 'Notice your patterns', subtitle: 'Mood, tension and thoughts — all in one place' }
    ];

    return slideAssets.map((src, i) => ({
      src,
      title: (copyArray[i]?.title || fallback[i]?.title || `Slide ${i + 1}`).toString(),
      subtitle: (copyArray[i]?.subtitle || fallback[i]?.subtitle || '').toString()
    }));
  }, [slideAssets, t]);

  useEffect(() => {
    setActiveIdx(0);
  }, [language]);

  useEffect(() => {
    if (prefersReducedMotion || isHover) return;
    const id = window.setInterval(() => {
      setActiveIdx((i) => (i + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [isHover, prefersReducedMotion, slides.length]);

  const goTo = (idx) => setActiveIdx((idx + slides.length) % slides.length);

  const onMouseMove = (e) => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    const rx = (0.5 - py) * 5;
    const ry = (px - 0.5) * 7;
    setTilt({ x: rx, y: ry });
  };

  const onMouseLeave = () => {
    setIsHover(false);
    setTilt({ x: 0, y: 0 });
  };

  const onTouchStart = (e) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    touchRef.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
  };

  const onTouchEnd = (e) => {
    const touch = e.changedTouches?.[0];
    if (!touch) return;
    const dx = touch.clientX - touchRef.current.x;
    const dy = touch.clientY - touchRef.current.y;
    const dt = Date.now() - touchRef.current.t;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (absX > 40 && absX > absY && dt < 600) {
      goTo(activeIdx + (dx < 0 ? 1 : -1));
    }
  };

  return (
    <div
      className={`app-showcase ${isHover ? 'is-hover' : ''} ${prefersReducedMotion ? 'is-reduced' : ''}`}
      ref={rootRef}
      onMouseEnter={() => setIsHover(true)}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        '--autoplay-ms': `${AUTOPLAY_MS}ms`
      }}
    >
      <div className="app-showcase__device">
        <div className="app-showcase__slides" aria-label="App preview screenshots">
          <div className={`app-showcase__progress ${isHover ? 'is-paused' : ''}`} key={activeIdx}>
            {slides.map((_, i) => (
              <div key={i} className={`app-showcase__progress-seg ${i === activeIdx ? 'is-active' : ''}`}>
                {i === activeIdx ? <span className="app-showcase__progress-fill" /> : null}
              </div>
            ))}
          </div>

          {slides[activeIdx] ? (
            <img
              className="app-showcase__img is-active"
              src={slides[activeIdx].src}
              srcSet={`${slides[activeIdx].src} 1284w`}
              alt={`AI PSY HELP app preview: ${slides[activeIdx].title}`}
              width="1284"
              height="2778"
              loading="eager"
              decoding="async"
              fetchpriority="high"
              sizes="(max-width: 600px) 56vw, (max-width: 1199px) 323px, 351px"
              draggable="false"
            />
          ) : null}
        </div>
      </div>

      <div className="app-showcase__caption">
        <div className="app-showcase__kicker">{t('home.app_showcase.kicker')}</div>
        <div className="app-showcase__title">{slides[activeIdx].title}</div>
        <div className="app-showcase__subtitle">{slides[activeIdx].subtitle}</div>
      </div>

      <div className="app-showcase__nav" aria-label="Preview navigation">
        {slides.map((s, i) => (
          <button
            key={s.title}
            type="button"
            className={`app-showcase__dot ${i === activeIdx ? 'is-active' : ''}`}
            aria-label={`Show ${s.title}`}
            aria-current={i === activeIdx ? 'true' : 'false'}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </div>
  );
}
